import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  TextField,
  Box,
  Paper,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoIcon from "@mui/icons-material/Info";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 12,
    padding: theme.spacing(2),
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: "#f8f9fa",
  borderRadius: 8,
  boxShadow: "none",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #2196F3 0%, #1976D2 100%)",
    borderRadius: "8px 8px 0 0",
  },
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  "& .MuiTypography-h5": {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  "& .MuiTypography-body1": {
    color: theme.palette.text.secondary,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    transition: "transform 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-2px)",
    },
    "& fieldset": {
      borderWidth: "2px",
    },
  },
}));

const FieldMappingModal = ({
  open,
  onClose,
  onBack,
  onConfirm,
  fiats,
  onFiatChange,
  types,
  onTypeChange,
  typeDescriptions,
}) => {
  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <HeaderSection>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            onClick={onBack}
            edge="start"
            sx={{ mr: 2, transform: "translateX(-8px)" }}
          >
            <ArrowBackIcon />
          </IconButton>
          <div>
            <Typography variant="h5">Configure Field Values</Typography>
            <Typography variant="body1">
              Define how your transaction types and fiat currencies should be
              identified in your data.
            </Typography>
          </div>
        </Box>
      </HeaderSection>

      <DialogContent sx={{ px: 0 }}>
        <Grid container spacing={3}>
          {/* Fiat Values Section */}
          <Grid xs={12}>
            <StyledPaper>
              <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                <Typography variant="h6" fontWeight={600}>
                  Fiat Values
                </Typography>
                <Tooltip
                  title="Comma-separated list of values that represent fiat currencies (e.g. USD,EUR,GBP) or currencies you want excluded from cost basis calculations (e.g., USDT,USD/STABLE)"
                  placement="right"
                >
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <StyledTextField
                fullWidth
                value={fiats}
                onChange={(e) => onFiatChange(e.target.value)}
                placeholder="e.g., USD,USDT,USD/STABLE"
                helperText="What assets would you like to leave out of cost basis calculation? For example, USD is a FIAT asset and would not have a cost basis. Enter comma-separated values that represent fiat currencies in your data"
              />
            </StyledPaper>
          </Grid>

          {/* Transaction Types Section */}
          <Grid xs={12}>
            <StyledPaper>
              <Box sx={{ mb: 3, display: "flex", alignItems: "center" }}>
                <Typography variant="h6" fontWeight={600}>
                  Transaction Types
                </Typography>
                <Tooltip
                  title="Define how different transaction types appear in your data"
                  placement="right"
                >
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Grid container spacing={3}>
                {Object.entries(typeDescriptions).map(
                  ([type, { description }]) => (
                    <Grid xs={12} key={type}>
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={500}
                          gutterBottom
                        >
                          {type}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          {description}
                        </Typography>
                        <StyledTextField
                          fullWidth
                          value={types[type]?.mapping || ""}
                          onChange={(e) => onTypeChange(type, e.target.value)}
                          placeholder={`e.g., ${type},${type.toLowerCase()}`}
                          helperText="Enter comma-separated values that represent this transaction type in your data"
                        />
                      </Box>
                    </Grid>
                  )
                )}
              </Grid>
            </StyledPaper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ mt: 3, px: 0, pb: 0 }}>
        <Button
          onClick={onClose}
          color="inherit"
          sx={{
            borderRadius: 2,
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="primary"
          variant="contained"
          sx={{
            borderRadius: 2,
            px: 3,
            boxShadow: "0 4px 12px rgba(33,150,243,0.2)",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 6px 16px rgba(33,150,243,0.3)",
            },
            transition: "all 0.2s ease-in-out",
          }}
        >
          Confirm & Preview
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default FieldMappingModal;
